import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { PENDING, IDLE } from "const"
import { MtAccount } from "server/models"
import { deepCopy, StateType } from "client/utils"
import {
    StoreType,
    UserProps,
    AccountList,
    AccountFilters,
    UserAccountList,

    LoadAllAccounts,
    LoadUserAccounts,
    LoadAllGroups,
    LoadAllUserAccountGroups,
    SortAccounts,
    ChangeAccountStatus,
    DeleteAccountUser,

    setAccountList,
    setUserAccountList,
    setGroupList,
    setAccountGroupList,
    setAssignedUserAccountIds,
    setAssignedUserAccountSort,
    setAssignedUserAccountFilters,
    setNotAssignedUserAccountSelected,
    addModal,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Loader,
    Button,
    TableSearchWrapper,
    Table,
    SortRows,
    ChangeStatus,
    DeleteRow,
} from "client/components"

import AccountSearch from "client/pages/search/accountSearch"
import Columns from "./columns"
import NotAssignedAccounts from "../notAssigned"

interface AssignedAccountsProps extends UserProps {
    showHeader?: boolean
}

const AssignedAccounts = ({
    userId = 0,
    showHeader = true,
}: AssignedAccountsProps) => {
    const userView = useSelector((state: StoreType) => state.user.userView)
    const accountList = useSelector((state: StoreType) => state.account.accountList)
    const userAccountList = useSelector((state: StoreType) => state.accountUser.userAccountList)
    const userAccountIds = userAccountList && userAccountList[userId] ? userAccountList[userId] : []

    const accountIds = useSelector((state: StoreType) => state.accountUser.assignedAccounts.accountIds)
    const sortColumn = useSelector((state: StoreType) => state.accountUser.assignedAccounts.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.accountUser.assignedAccounts.sortOrder)
    const filters = useSelector((state: StoreType) => state.accountUser.assignedAccounts.filters)
    const reloadList = useSelector((state: StoreType) => state.accountUser.reloadUserAccountList)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        const promiseArray = [
            LoadAllAccounts().then((res) => {
                dispatch(setAccountList(res))
            }),
            LoadUserAccounts(userId).then((userAccounts) => {
                const userAccountListCopy: UserAccountList = userAccountList ? deepCopy(userAccountList) : {}
                userAccountListCopy[userId] = userAccounts
                dispatch(setUserAccountList(userAccountListCopy))
            }),
            LoadAllGroups().then((res) => {
                dispatch(setGroupList(res))
                LoadAllUserAccountGroups(userId, res).then((accountGroupList) => {
                    dispatch(setAccountGroupList(accountGroupList))
                })
            }),
        ]
        Promise.all(promiseArray).then(() => {
            setLoadState(IDLE)
        })
    }, [reloadList])

    useEffect(() => {
        const sortedAccountIds = SortAccounts(Object.values(accountList), sortColumn, sortOrder, filters)
        const sortedAssigned = sortedAccountIds.filter((accountId) => userAccountIds.includes(accountId))
        dispatch(setAssignedUserAccountIds(sortedAssigned))
    }, [userAccountList, sortColumn, sortOrder, filters])

    const onSort: SortRows = (column: keyof MtAccount, order) => {
        dispatch(setAssignedUserAccountSort(column, order))
    }

    const onFilter = (newFilters: AccountFilters) => {
        dispatch(setAssignedUserAccountFilters(newFilters))
    }

    const onStatusChange: ChangeStatus = (account: MtAccount, callback) => {
        ChangeAccountStatus(account).then((status) => {
            callback(() => {
                const accountListCopy: AccountList = deepCopy(accountList)
                accountListCopy[account.id] = {
                    ...accountListCopy[account.id],
                    status,
                }
                dispatch(setAccountList(accountListCopy))
            })
        })
    }

    const onDelete: DeleteRow = (account: MtAccount, callback) => {
        DeleteAccountUser(userId, account.id).then((removedRows) => {
            const success = removedRows === 1
            callback(success, () => {
                const userAccountListCopy: UserAccountList = userAccountList ? deepCopy(userAccountList) : {}
                userAccountListCopy[userId] = userAccountIds.filter((id) => id !== account.id)
                dispatch(setUserAccountList(userAccountListCopy))
            })
        })
    }

    const columns = Columns(userId)

    return (
        <Container>
            {
                showHeader && (
                    <PageHeader>
                        <PageTitle>{`${userView.firstName} ${userView.lastName} ${lang.accounts}`}</PageTitle>
                    </PageHeader>
                )
            }
            <PageContent>
                {loadState === PENDING && <Loader />}
                <TableSearchWrapper>
                    <AccountSearch
                        id="assignedUserAccounts"
                        rightOffset
                        filters={filters}
                        onFilter={onFilter}
                    />
                    <Button
                        variant="success"
                        onClick={() => {
                            dispatch(setNotAssignedUserAccountSelected(null))
                            dispatch(addModal({
                                key: "notAssignedAccounts",
                                content: <NotAssignedAccounts userId={userId} />,
                            }))
                        }}
                    >
                        {lang.add}
                    </Button>
                </TableSearchWrapper>
                <Table
                    columns={columns}
                    dataList={accountList}
                    dataIds={accountIds}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                    rowSize="md"
                    showPagination
                    onSort={onSort}
                    onStatusChange={onStatusChange}
                    onDelete={onDelete}
                />
            </PageContent>
        </Container>
    )
}
export default AssignedAccounts
